/**
 * S23-G Anti-detection signal catalog (frontend mirror).
 *
 * Source of truth: `rpvpn-client libs/anti-detection-catalog.ts`
 * (S23-D registry, commit 9fa53ec). The BO controller
 * `ANTI_DETECTION_SIGNAL_KEYS` mirrors the same key set under
 * `vpn-manage-server/src/service/anti_detection_telemetry.service.ts`.
 *
 * Any drift between these three tables is a phantom-completion
 * vector per [[feedback_phantom_completion_audit_runtime]] — the
 * audit script `scripts/audit/anti-detection-3way-parity.sh` runs
 * a literal-key comparison between BO controller / this catalog /
 * S23-D registry on every CI run.
 *
 * UI consumes `verifyStatus` to drive the GO / CONDITIONAL / DORMANT
 * badge per spec docs/architecture/anti-detection-telemetry-dashboard.md
 * §3.2. A signal whose `perScopeGate` contains a PROHIBIT entry also
 * surfaces a red PROHIBIT badge next to the main one.
 *
 * Sprint 24 T1-E owns this catalog mirror. DO NOT add new keys here
 * without an upstream S23-D registry update — the parity audit will
 * trip and the BO controller will silently drop the row at runtime.
 */

export type AntiDetectionSignalKey =
  | 'utls.fingerprint'
  | 'mux.padding'
  | 'record_fragment.tls'
  | 'anytls.junk'
  | 'cover_traffic.idle'
  | 'daita.maybenot'
  | 'dns.odoh'
  | 'dns.transport_mix'
  | 'ech.client_hello'
  | 'hy2.salamander'
  | 'anytls.alternative_ports'
  | 'anytls.backup_domains'
  | 'cellular.drop_list'
  | 'cellular.slow_proxy_drop'
  | 'cellular.outbound_ban'
  | 'urltest.last_resort_priority';

export type AntiDetectionVerifyStatus =
  | 'production-verified'
  | 'cn-vantage-tested'
  | 'staging-tested'
  | 'untested';

export type AntiDetectionCategory =
  | 'tls-fingerprint'
  | 'traffic-shape'
  | 'padding'
  | 'dns'
  | 'protocol-fallback';

export type AntiDetectionDefaultGate = 'always' | 'opt-in' | 'per-scope';

export interface AntiDetectionCatalogEntry {
  key: AntiDetectionSignalKey;
  category: AntiDetectionCategory;
  verifyStatus: AntiDetectionVerifyStatus;
  defaultGate: AntiDetectionDefaultGate;
  /** Human label (Traditional Chinese — matches BO admin locale). */
  label: string;
  /** Short rationale shown in tooltip / drill-down header. */
  description: string;
  /** Per-scope PROHIBIT entries — surfaces red badge. */
  prohibitScopes?: string[];
}

/**
 * Mirror of S23-D catalog ordering — keep insertion order stable so
 * the 16-tile grid layout is deterministic between releases.
 */
export const ANTI_DETECTION_CATALOG: readonly AntiDetectionCatalogEntry[] = [
  {
    key: 'utls.fingerprint',
    category: 'tls-fingerprint',
    verifyStatus: 'production-verified',
    defaultGate: 'always',
    label: 'uTLS 指紋',
    description: '模擬 Chrome JA3 指紋，全平台一致對抗 TLS 握手指紋識別。',
  },
  {
    key: 'mux.padding',
    category: 'traffic-shape',
    verifyStatus: 'production-verified',
    defaultGate: 'always',
    label: 'Mux Padding',
    description: 'sing-mux padding + min/max 1-4 stream，混淆封包大小特徵。',
  },
  {
    key: 'record_fragment.tls',
    category: 'traffic-shape',
    verifyStatus: 'cn-vantage-tested',
    defaultGate: 'per-scope',
    label: 'TLS Record 切片',
    description: 'TLS record fragment 切碎，避開單封包 SNI 指紋；per-scope 控管。',
  },
  {
    key: 'anytls.junk',
    category: 'traffic-shape',
    verifyStatus: 'production-verified',
    defaultGate: 'opt-in',
    label: 'AnyTLS Junk',
    description: 'AnyTLS junk byte 填充對抗流量特徵分析。',
  },
  {
    key: 'cover_traffic.idle',
    category: 'traffic-shape',
    verifyStatus: 'production-verified',
    defaultGate: 'opt-in',
    label: '空閒覆蓋流量',
    description: '閒置時段心跳/封面流量，破壞長期流量空窗指紋。',
  },
  {
    key: 'daita.maybenot',
    category: 'traffic-shape',
    verifyStatus: 'staging-tested',
    defaultGate: 'opt-in',
    label: 'DAITA Maybenot',
    description: 'Mullvad DAITA / maybenot v3 padding 機器學習對抗模型。',
  },
  {
    key: 'dns.odoh',
    category: 'dns',
    verifyStatus: 'staging-tested',
    defaultGate: 'opt-in',
    label: 'Oblivious DoH',
    description: 'ODoH 隱藏 DNS 查詢來源 IP，限 staging 驗證中。',
  },
  {
    key: 'dns.transport_mix',
    category: 'dns',
    verifyStatus: 'production-verified',
    defaultGate: 'opt-in',
    label: 'DNS 傳輸混合',
    description: 'DoH3/DoQ/DoT 隨機混合 transport，避開單一指紋掃描。',
  },
  {
    key: 'ech.client_hello',
    category: 'tls-fingerprint',
    verifyStatus: 'staging-tested',
    defaultGate: 'opt-in',
    label: 'ECH (Encrypted ClientHello)',
    description: 'TLS 1.3 ECH 加密握手 SNI，需 staging 驗證 GFW 接受度。',
  },
  {
    key: 'hy2.salamander',
    category: 'padding',
    verifyStatus: 'staging-tested',
    defaultGate: 'opt-in',
    label: 'Hysteria2 Salamander',
    description: 'Hy2 Salamander 混淆器；libbox with_quic tag pending。',
  },
  {
    key: 'anytls.alternative_ports',
    category: 'protocol-fallback',
    verifyStatus: 'production-verified',
    defaultGate: 'opt-in',
    label: 'AnyTLS 備援端口',
    description: '443 被封時切換備用端口陣列，opt-in 開啟。',
  },
  {
    key: 'anytls.backup_domains',
    category: 'protocol-fallback',
    verifyStatus: 'staging-tested',
    defaultGate: 'opt-in',
    label: 'AnyTLS 備用域名',
    description: 'SNI 被封時 backupDomains 輪替，限 staging 環境驗證。',
  },
  {
    key: 'cellular.drop_list',
    category: 'protocol-fallback',
    verifyStatus: 'production-verified',
    defaultGate: 'always',
    label: '蜂窩網路降級清單',
    description: '行動網偵測到指定協議異常時自動 drop，提升重連率。',
  },
  {
    key: 'cellular.slow_proxy_drop',
    category: 'protocol-fallback',
    verifyStatus: 'production-verified',
    defaultGate: 'always',
    label: '蜂窩慢代理切換',
    description: '行動網延遲超閾值自動切換協議（Phase 18 Fix D 上線）。',
  },
  {
    key: 'cellular.outbound_ban',
    category: 'protocol-fallback',
    verifyStatus: 'production-verified',
    defaultGate: 'always',
    label: '蜂窩 Outbound 禁用',
    description: '蜂窩運營商封鎖協議時即時 quarantine outbound。',
  },
  {
    key: 'urltest.last_resort_priority',
    category: 'protocol-fallback',
    verifyStatus: 'production-verified',
    defaultGate: 'always',
    label: 'URLTest 末位回退',
    description: 'VLESS Workers 作為終極 fallback，URLTest 60s 輪詢容災。',
  },
];

export const ANTI_DETECTION_SIGNAL_KEYS: readonly AntiDetectionSignalKey[] =
  ANTI_DETECTION_CATALOG.map((entry) => entry.key);

export type SignalOutcome = 'fire' | 'refuse' | 'degrade';

export const SIGNAL_OUTCOMES: readonly SignalOutcome[] = [
  'fire',
  'refuse',
  'degrade',
];

export type AntiDetectionSource =
  | 'audit_log'
  | 'connectivity_event'
  | 'worker_log'
  | 'fire_marker_event';

export const ANTI_DETECTION_SOURCES: readonly AntiDetectionSource[] = [
  'audit_log',
  'connectivity_event',
  'worker_log',
  'fire_marker_event',
];

export interface AntiDetectionTelemetryRow {
  signalKey: AntiDetectionSignalKey | string;
  outcome: SignalOutcome;
  source: AntiDetectionSource;
  nodeId: number;
  brand: string;
  hourBucket: string;
  count: number;
  lastSeenAt: string;
}

export interface AntiDetectionTelemetryResponse {
  brand: string;
  nodeId: number | null;
  from: string;
  to: string;
  rowCount: number;
  rows: AntiDetectionTelemetryRow[];
}

/**
 * Badge mapping per spec §3.2 — pure function so unit tests can pin
 * the colour contract independently of the visual layer.
 */
export interface BadgeDescriptor {
  label: 'GO' | 'CONDITIONAL' | 'DORMANT' | 'UNTESTED';
  color: 'green' | 'gold' | 'default' | 'orange';
  tooltip: string;
}

export function describeBadge(
  verifyStatus: AntiDetectionVerifyStatus
): BadgeDescriptor {
  switch (verifyStatus) {
    case 'production-verified':
      return {
        label: 'GO',
        color: 'green',
        tooltip: '生產線上已驗證的 anti-detection 信號。',
      };
    case 'cn-vantage-tested':
      return {
        label: 'CONDITIONAL',
        color: 'gold',
        tooltip: 'GFW vantage 探針已捕獲，正式啟用採 per-scope 控管。',
      };
    case 'staging-tested':
      return {
        label: 'DORMANT',
        color: 'default',
        tooltip: '本機 staging 已驗證但尚未線上開啟，預設 opt-in OFF。',
      };
    case 'untested':
    default:
      return {
        label: 'UNTESTED',
        color: 'orange',
        tooltip: '尚未驗證的候選信號，僅供 catalog 暫存。',
      };
  }
}
